// src/components/SmartFlow/workflowValidator.js

/**
 * Validates a workflow structure before save/execution
 * @param {Array} nodes - Array of workflow nodes
 * @param {Array} edges - Array of workflow edges
 * @returns {Object} { isValid: boolean, errors: string[] }
 */
export const validateWorkflow = (nodes, edges) => {
  const errors = [];

  // Rule 1: Must have at least one node
  if (!nodes || nodes.length === 0) {
    errors.push("Workflow must have at least one node");
    return { isValid: false, errors };
  }

  // Rule 2: Exactly one trigger node
  const triggerNodes = nodes.filter((node) => node.type === "TRIGGER");
  if (triggerNodes.length === 0) {
    errors.push("Workflow must have exactly one trigger node");
  } else if (triggerNodes.length > 1) {
    errors.push("Workflow can only have one trigger node");
  }

  // Rule 3: At least one end node
  const endNodes = nodes.filter((node) => node.type === "END");
  if (endNodes.length === 0) {
    errors.push("Workflow must have at least one end node");
  }

  // Rule 4: No orphaned nodes (all nodes should be connected)
  const connectedNodeIds = new Set();
  edges.forEach((edge) => {
    connectedNodeIds.add(edge.source);
    connectedNodeIds.add(edge.target);
  });

  // Trigger nodes don't need incoming connections, end nodes don't need outgoing
  const orphanedNodes = nodes.filter((node) => {
    const isConnected = connectedNodeIds.has(node.id);
    const isTrigger = node.type === "TRIGGER";
    const isEnd = node.type === "END";

    // Trigger only needs outgoing, End only needs incoming
    if (isTrigger) {
      return !edges.some((e) => e.source === node.id);
    }
    if (isEnd) {
      return !edges.some((e) => e.target === node.id);
    }

    // Other nodes need at least one connection
    return !isConnected;
  });

  if (orphanedNodes.length > 0) {
    const orphanedLabels = orphanedNodes.map((n) => n.type).join(", ");
    errors.push(`Orphaned nodes detected: ${orphanedLabels}`);
  }

  // Rule 5: Condition nodes must have both true and false edges
  const conditionNodes = nodes.filter((node) => node.type === "CONDITION");
  conditionNodes.forEach((condNode) => {
    const outgoingEdges = edges.filter((e) => e.source === condNode.id);
    const hasTrueEdge = outgoingEdges.some((e) => e.sourceHandle === "if_true");
    const hasFalseEdge = outgoingEdges.some((e) => e.sourceHandle === "if_false");

    if (!hasTrueEdge) {
      errors.push(`Condition node must have a TRUE branch connection`);
    }
    if (!hasFalseEdge) {
      errors.push(`Condition node must have a FALSE branch connection`);
    }
  });

  // Rule 6: No circular dependencies (cycles detection using DFS)
  const hasCycle = detectCycle(nodes, edges);
  if (hasCycle) {
    errors.push("Workflow contains circular dependencies (infinite loop detected)");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Detect cycles in workflow graph using Depth-First Search
 * @param {Array} nodes - Array of workflow nodes
 * @param {Array} edges - Array of workflow edges
 * @returns {boolean} - True if cycle detected
 */
const detectCycle = (nodes, edges) => {
  const visited = new Set();
  const recursionStack = new Set();

  // Build adjacency list
  const adjacencyList = {};
  nodes.forEach((node) => {
    adjacencyList[node.id] = [];
  });
  edges.forEach((edge) => {
    if (adjacencyList[edge.source]) {
      adjacencyList[edge.source].push(edge.target);
    }
  });

  const dfs = (nodeId) => {
    visited.add(nodeId);
    recursionStack.add(nodeId);

    const neighbors = adjacencyList[nodeId] || [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        if (dfs(neighbor)) {
          return true; // Cycle detected
        }
      } else if (recursionStack.has(neighbor)) {
        return true; // Back edge found (cycle)
      }
    }

    recursionStack.delete(nodeId);
    return false;
  };

  // Check all nodes (workflow might have disconnected components)
  for (const node of nodes) {
    if (!visited.has(node.id)) {
      if (dfs(node.id)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Quick validation for specific checks
 */
export const hasRequiredNodes = (nodes) => {
  const hasTrigger = nodes.some((n) => n.type === "TRIGGER");
  const hasEnd = nodes.some((n) => n.type === "END");
  return hasTrigger && hasEnd;
};

export const getNodeCount = (nodes) => {
  return {
    total: nodes.length,
    triggers: nodes.filter((n) => n.type === "TRIGGER").length,
    actions: nodes.filter((n) => n.type.startsWith("ACTION_")).length,
    conditions: nodes.filter((n) => n.type === "CONDITION").length,
    delays: nodes.filter((n) => n.type === "DELAY").length,
    ends: nodes.filter((n) => n.type === "END").length,
  };
};
